import {
  BookOpenCheck,
  CheckCircle2,
  ExternalLink,
  FileText,
  Link2,
  Mic,
  Palette,
  Paperclip,
  Shirt,
  X,
} from "lucide-react";

import { TResponseOrderVideos } from "@/types/projectType";
import { FileDetails } from "@/types/types";
import { getPrimaryActorDetails } from "../utils/orderVideoHelpers";

const optionLabel = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";

  const option = value as {
    title?: string;
    label?: string;
    id?: string;
  };
  return (option.title || option.label || option.id || "").trim();
};

const fileUrl = (file: FileDetails) => file.live_url || file.local_url || "";
const isImage = (file: FileDetails) =>
  file.type?.startsWith("image/") ||
  /\.(png|jpe?g|webp|gif)$/i.test(file.name || "");

const displaySize = (bytes?: number) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const readableLink = (link: string) => {
  try {
    return new URL(link).hostname.replace(/^www\./, "");
  } catch {
    return link;
  }
};

const DetailValue = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div className="rounded-lg border border-[#EAECF0] bg-white px-3 py-2.5">
    <p className="text-[10px] font-medium uppercase tracking-[0.04em] text-[#98A2B3]">
      {label}
    </p>
    <p className="mt-1 text-xs font-medium text-[#344054]">
      {value || "Not specified"}
    </p>
  </div>
);

const SectionHeader = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className="mb-4 flex items-start gap-3">
    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#17457D0F] text-[#17457D]">
      {icon}
    </div>
    <div>
      <h3 className="text-sm font-semibold text-[#101828]">{title}</h3>
      <p className="mt-0.5 text-xs leading-5 text-[#667085]">{description}</p>
    </div>
  </div>
);

const NoteBox = ({
  label,
  children,
}: {
  label: string;
  children?: string | null;
}) => (
  <div className="rounded-lg border border-[#E4E7EC] bg-[#F9FAFB] px-3.5 py-3">
    <p className="text-[11px] font-medium text-[#667085]">{label}</p>
    <p className="mt-1 whitespace-pre-wrap text-xs leading-5 text-[#344054]">
      {children?.trim() || "No notes provided"}
    </p>
  </div>
);

const AttachmentGroup = ({
  title,
  files = [],
  links = [],
}: {
  title: string;
  files?: FileDetails[];
  links?: string[];
}) => {
  const cleanLinks = links.filter(Boolean);
  const hasAttachments = files.length > 0 || cleanLinks.length > 0;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-medium text-[#344054]">{title}</p>
        {hasAttachments && (
          <span className="rounded-full bg-[#F2F4F7] px-2 py-0.5 text-[10px] font-medium text-[#667085]">
            {files.length + cleanLinks.length} item
            {files.length + cleanLinks.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {!hasAttachments ? (
        <div className="rounded-lg border border-dashed border-[#D0D5DD] px-3 py-4 text-center text-xs text-[#98A2B3]">
          Nothing attached
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file, index) => {
            const url = fileUrl(file);
            const content = (
              <>
                <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#F2F4F7] text-[#667085]">
                  {isImage(file) && url ? (
                    <img
                      src={url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <FileText className="size-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-[#344054]">
                    {file.name || `Attachment ${index + 1}`}
                  </p>
                  <p className="mt-0.5 text-[10px] text-[#98A2B3]">
                    {displaySize(file.size) || file.type || "File"}
                  </p>
                </div>
                {url && <ExternalLink className="size-4 text-[#667085]" />}
              </>
            );

            return url ? (
              <a
                key={`${file.name}-${index}`}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded-lg border border-[#EAECF0] bg-white p-2.5 transition-colors hover:border-[#B8C7D9] hover:bg-[#F9FAFB]"
              >
                {content}
              </a>
            ) : (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-3 rounded-lg border border-[#EAECF0] bg-white p-2.5"
              >
                {content}
              </div>
            );
          })}

          {cleanLinks.map((link, index) => (
            <a
              key={`${link}-${index}`}
              href={link}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 rounded-lg border border-[#EAECF0] bg-white p-3 transition-colors hover:border-[#B8C7D9] hover:bg-[#F9FAFB]"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#17457D0F] text-[#17457D]">
                <Link2 className="size-4" />
              </span>
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-[#344054]">
                {readableLink(link)}
              </span>
              <ExternalLink className="size-4 text-[#667085]" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const CreativeBriefModal = ({
  video,
  onClose,
}: {
  video: TResponseOrderVideos;
  onClose: () => void;
}) => {
  const actor = getPrimaryActorDetails(video.actorDetails);
  const editing = video.videoEditingDetails;
  const wardrobe = actor?.wardrobeStyle?.map(optionLabel).filter(Boolean) ?? [];
  const addOns = editing.addOns ?? [];

  const productPronunciations = (
    video.scriptDetails?.featuredProduct ?? []
  ).filter((p) => p.pronunciation?.live_url);

  return (
    <section className="flex max-h-[88vh] flex-col overflow-hidden rounded-2xl bg-white">
      <div className="flex shrink-0 items-start justify-between border-b border-[#EAECF0] px-6 py-5">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="rounded-full bg-[#FF6600] px-2 py-1 text-[10px] font-medium text-white">
              Creative brief
            </span>
            <span className="text-xs text-[#98A2B3]">
              Video {video.video_id}
            </span>
          </div>
          <h2 className="text-xl font-semibold tracking-[-0.4px] text-[#101828]">
            Production direction
          </h2>
          <p className="mt-1 text-xs leading-5 text-[#667085]">
            Everything the client shared for wardrobe, filming, and editing.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close creative brief"
          onClick={onClose}
          className="flex size-9 items-center justify-center rounded-lg text-[#667085] transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]"
        >
          <X className="size-5" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto bg-[#F9FAFB] p-6">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <DetailValue label="Format" value={editing.format} />
          <DetailValue label="Background" value={editing.background} />
          <DetailValue label="Editing style" value={editing.style} />
          <DetailValue
            label="Logo in video"
            value={
              editing.useLogoInVideo === undefined
                ? undefined
                : editing.useLogoInVideo
                  ? "Yes"
                  : "No"
            }
          />
        </div>

        <div className="rounded-xl border border-[#E4E7EC] bg-white p-5">
          <SectionHeader
            icon={<Shirt className="size-4" />}
            title="Wardrobe & on-camera direction"
            description="Talent preferences and the client's wardrobe instructions."
          />

          <div className="mt-4">
            <p className="mb-2 text-[11px] font-medium text-[#667085]">
              Wardrobe styles
            </p>
            {wardrobe.length ? (
              <div className="flex flex-wrap gap-2">
                {wardrobe.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-[#D0D5DD] bg-white px-3 py-1 text-xs font-medium text-[#344054]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#98A2B3]">No styles specified</p>
            )}
          </div>

          <div className="mt-4">
            <NoteBox label="Wardrobe / actor comments">
              {actor?.additionalNotes}
            </NoteBox>
          </div>
        </div>

        <div className="rounded-xl border border-[#E4E7EC] bg-white p-5">
          <SectionHeader
            icon={<Palette className="size-4" />}
            title="Editing direction"
            description="References, notes, and supporting material for the edit."
          />

          <div className="grid gap-4 md:grid-cols-2">
            <AttachmentGroup
              title="Editing references"
              files={editing.reference?.files}
              links={editing.reference?.links}
            />
            <AttachmentGroup
              title="Additional files & links"
              files={editing.additionalFilesAndLinks?.files}
              links={editing.additionalFilesAndLinks?.links}
            />
          </div>

          <div className="mt-4">
            <NoteBox label="Editing notes">{editing.additionalNotes}</NoteBox>
          </div>
        </div>

        <div className="rounded-xl border border-[#E4E7EC] bg-white p-5">
          <SectionHeader
            icon={<BookOpenCheck className="size-4" />}
            title="Brand guidelines"
            description="Brand rules and assets supplied by the client."
          />

          <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#F9FAFB] px-3.5 py-3">
            <CheckCircle2
              className={`size-4 ${
                editing.hasBrandGuidelines ? "text-[#039855]" : "text-[#98A2B3]"
              }`}
            />
            <p className="text-xs font-medium text-[#344054]">
              {editing.hasBrandGuidelines
                ? "Brand guidelines were provided"
                : "No brand guidelines specified"}
            </p>
          </div>

          <AttachmentGroup
            title="Guideline attachments"
            files={editing.guidelinesAttachments?.files}
            links={editing.guidelinesAttachments?.links}
          />
        </div>

        <div className="rounded-xl border border-[#E4E7EC] bg-white p-5">
          <SectionHeader
            icon={<Paperclip className="size-4" />}
            title="Additional order instructions"
            description="Extra production details and quick-order selections."
          />

          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <DetailValue
              label="Logo position"
              value={optionLabel(editing.logoPositionInVideo)}
            />
            <DetailValue
              label="Product shipping"
              value={optionLabel(editing.productShippingOption)}
            />
            <DetailValue
              label="Feature product"
              value={optionLabel(editing.willFeatureProduct)}
            />
            <DetailValue
              label="Copy settings"
              value={
                editing.shouldHideCopyOption === undefined
                  ? undefined
                  : editing.shouldHideCopyOption
                    ? "Hidden"
                    : "Available"
              }
            />
          </div>

          {editing.pronunciation && (
            <div className="mt-4">
              <AttachmentGroup
                title="Pronunciation guide"
                files={[editing.pronunciation]}
              />
            </div>
          )}

          {productPronunciations.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-[#344054]">
                Product name pronunciation
              </p>
              <div className="space-y-2">
                {productPronunciations.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-lg border border-[#EAECF0] bg-white p-2.5"
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#17457D0F] text-[#17457D]">
                      <Mic className="size-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="mb-1 truncate text-[11px] font-medium text-[#344054]">
                        {p.name}
                      </p>
                      <audio
                        controls
                        src={p.pronunciation.live_url as string}
                        className="h-7 w-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {addOns.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-[11px] font-medium text-[#667085]">
                Add-ons
              </p>
              <div className="flex flex-wrap gap-2">
                {addOns.map((addOn) => (
                  <span
                    key={addOn.id}
                    className="rounded-full bg-[#FFF4EC] px-3 py-1 text-xs font-medium text-[#B54708]"
                  >
                    {addOn.title}
                    {addOn.quantity > 1 ? ` ×${addOn.quantity}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <NoteBox label="Additional note">{editing.additionalNote}</NoteBox>
          </div>
          <div className="mt-4">
            <AttachmentGroup
              title="Additional instruction attachments"
              files={editing.additionalInstructionAttachments?.files}
              links={editing.additionalInstructionAttachments?.links}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreativeBriefModal;
